import { useState, useCallback, useRef, useEffect } from "react";
import { fget } from "../../API/callsAPI";

const BATCH_SIZE = 15;
const EXPIRE_TIME = 54000; //expiery time is 15mins

const useUrlBatchLoader = (
  shotsLineUp,
  params,
  setThumbnails,
  setShotsLineUp,
  setResetAll,
) => {
  const [currentUrlBatch, setCurrentUrlBatch] = useState(0);
  const [loadedUrlBatches, setLoadedUrlBatches] = useState(new Set());
  const [startedLoadingBatches, setStartedLoadingBatches] = useState(new Set()); // Track which batches we've started loading
  const [loadedUrls, setLoadedUrls] = useState({});
  const [forceReload, setForceReload] = useState(0);

  const cleanupBatches = useCallback(
    (currentUrlBatch) => {
      const batchesToKeep = [currentUrlBatch, currentUrlBatch + 1].map(Number); // Ensure integers
      if (Object.keys(loadedUrls).length === 0) {
        return;
      }

      // Cleanup video sources
      const localLoadedUrls = Object.entries(loadedUrls).filter(
        ([shotId, value]) => {
          if (!batchesToKeep.includes(parseInt(value.urlBatchNumber))) {
            return false;
          }
          return true;
        },
      );
      setLoadedUrls(Object.fromEntries(localLoadedUrls));
      setLoadedUrlBatches(new Set(batchesToKeep)); // Already integers from map(Number) above
    },
    [loadedUrls, setLoadedUrlBatches],
  );

  async function fetchShotSources(shotList) {
    const queryParam = shotList.map((eachShot) => `shot_version=${eachShot}`);
    const signedUrlObj = {};
    try {
      const query = queryParam.join("&");
      const signedUrls = await fget({
        url: `sequencers/timelines/${params.playlistId}/files?${query}`,
      });
      if (signedUrls.data?.results?.length) {
        const sThumbnails = signedUrls.data?.results?.map(
          (sin) => sin.thumbnail,
        );
        setThumbnails((prev) => [...prev, ...sThumbnails]);
        for (let i = 0; i < signedUrls.data?.results?.length; i++) {
          signedUrlObj[signedUrls.data?.results[i].shot_version] =
            signedUrls.data?.results[i];
        }
      }
    } catch (error) {
      console.log(error);
    }
    return Promise.resolve(signedUrlObj);
  }

  const loadBatch = async (batchNum) => {
    const batchNumber = parseInt(batchNum);
    const shotReps = shotsLineUp.filter(
      (shotRep) => parseInt(shotRep.urlBatchNumber) === batchNumber,
    );
    const ArrayOfShotId = shotReps.map((eachShot) => eachShot?.shotVersion?.id);
    try {
      const result = await fetchShotSources(ArrayOfShotId);
      const localLoadedUrls = {};
      Object.entries(result).forEach(([shotId, value]) => {
        const shotRep = shotReps.find((shot) => {
          return parseInt(shot.shotVersion.id) === parseInt(shotId);
        });
        if (shotRep) {
          localLoadedUrls[shotId] = {
            url: value.signed_url,
            loadTime: Date.now(),
            expireTime: Date.now() + EXPIRE_TIME,
            urlBatchNumber: batchNumber, // Already an integer
            videoBatchNumber: parseInt(shotRep.videoBatchNumber),
          };
        }
      });
      setLoadedUrls((prev) => ({ ...prev, ...localLoadedUrls }));

      setLoadedUrlBatches(
        (prevLoadedBatches) => new Set([...prevLoadedBatches, batchNumber]),
      );
      //remove this batch from started loading batches
      setStartedLoadingBatches(
        (prev) =>
          new Set([...prev].filter((batch) => parseInt(batch) !== batchNumber)),
      );
    } catch (error) {
      console.error("Error generating shots", error);
    }
  };

  const forceReloadBatch = () => {
    setLoadedUrls({});
    setLoadedUrlBatches(new Set());
    setStartedLoadingBatches(new Set());
    setForceReload(forceReload + 1);
  };

  useEffect(() => {
    if (
      shotsLineUp.length === 0 &&
      loadedUrls.length === 0 &&
      currentUrlBatch === 0 &&
      loadedUrlBatches.size === 0 &&
      startedLoadingBatches.size === 0
    ) {
      setResetAll((prev) => prev + 1);
    }
  }, [
    shotsLineUp,
    loadedUrls,
    currentUrlBatch,
    loadedUrlBatches,
    startedLoadingBatches,
  ]);

  const resetUrlBatchStates = useCallback(() => {
    setShotsLineUp([]);
    setThumbnails([]);
    setLoadedUrls({});
    setCurrentUrlBatch(0);
    setLoadedUrlBatches(new Set());
    setStartedLoadingBatches(new Set());
  }, [setThumbnails]);

  useEffect(() => {
    const currentBatch = parseInt(currentUrlBatch);
    if (
      loadedUrlBatches.has(currentBatch) &&
      loadedUrlBatches.has(currentBatch + 1) &&
      loadedUrlBatches.size > 2
    ) {
      cleanupBatches(currentBatch);
    }
  }, [currentUrlBatch, loadedUrlBatches]);

  //here everytime current batch changes we make api call to get current batch urls and preload next batch urls if they dont exsist
  useEffect(() => {
    if (shotsLineUp.length > 0 || forceReload > 0) {
      const currentBatch = parseInt(currentUrlBatch);
      // Only start loading if we haven't started loading this batch yet
      if (
        !startedLoadingBatches.has(currentBatch) &&
        !loadedUrlBatches.has(currentBatch)
      ) {
        setStartedLoadingBatches((prev) => new Set([...prev, currentBatch]));
        loadBatch(currentBatch);
      }

      //   After 5 seconds, load next batch if it exists and isn't loaded
      const timer = setTimeout(() => {
        const nextBatchExists = shotsLineUp.some(
          (value) => parseInt(value.urlBatchNumber) === currentBatch + 1,
        );

        if (
          nextBatchExists &&
          !startedLoadingBatches.has(currentBatch + 1) &&
          !loadedUrlBatches.has(currentBatch + 1)
        ) {
          setStartedLoadingBatches(
            (prev) => new Set([...prev, currentBatch + 1]),
          );
          loadBatch(currentBatch + 1);
        }
      }, 8000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [currentUrlBatch, shotsLineUp, forceReload]);

  useEffect(() => {
    return () => {
      resetUrlBatchStates();
    };
  }, []);

  return {
    currentUrlBatch,
    setCurrentUrlBatch,
    loadedUrls,
    startedLoadingBatches,
    setLoadedUrls,
    forceReloadBatch,
    resetUrlBatchStates,
  };
};

export default useUrlBatchLoader;
